import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMsg } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Settings as SettingsIcon, Shield, CheckCircle, Tag, MapPin, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useConfig } from "../lib/ConfigContext";

/**
 * Admin Settings Tab (v2.20)
 * SSOT global flags. Edits trigger ConfigContext refresh so all dashboards re-fetch.
 */
export default function AdminSettingsTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const { refresh } = useConfig();

  const load = async () => {
    try {
      const r = await api.get("/admin/settings");
      setS(r.data);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load settings"));
    }
  };
  useEffect(() => { load(); }, []);

  const update = async (patch) => {
    setBusy(true);
    try {
      const r = await api.patch("/admin/settings", patch);
      setS(r.data);
      toast.success("Saved");
      refresh?.();  // re-hydrate global config so dashboards see new values
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    }
    setBusy(false);
  };

  if (!s) return <div className="text-[#A1A1AA] text-sm p-6 text-center bg-white border border-dashed border-[#E4E4E7] rounded-sm">Loading…</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-settings-tab">
      <div className="flex items-center gap-2">
        <SettingsIcon size={16} className="text-[#0055FF]" />
        <h2 className="font-heading font-bold text-xl tracking-tight">Platform Settings</h2>
        <span className="text-[11px] text-[#A1A1AA]">Single source of truth — applies platform-wide on save</span>
      </div>

      {/* Free Listing Mode */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="setting-free-mode">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-heading font-semibold text-[15px] tracking-tight inline-flex items-center gap-2">
              <Shield size={14} className="text-[#0055FF]" /> Free Listing Mode
            </div>
            <p className="text-[13px] text-[#52525B] mt-1 max-w-md">
              When ON, dealers &amp; builders can submit unlimited listings &amp; projects without any plan/quota check.
            </p>
            <div className="mt-2 text-[11px] uppercase tracking-wider font-semibold inline-flex items-center gap-1.5">
              {s.free_mode ? (
                <span className="text-[#15803D] inline-flex items-center gap-1"><CheckCircle size={10} /> Currently ON — unlimited mode</span>
              ) : (
                <span className="text-[#92400E]">Currently OFF — plan/quota gate active</span>
              )}
            </div>
          </div>
          <Switch
            checked={!!s.free_mode}
            onCheckedChange={(v) => update({ free_mode: v })}
            disabled={busy}
            data-testid="free-mode-switch"
          />
        </div>
      </div>

      {/* Max free listings */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="setting-max-free">
        <div className="font-heading font-semibold text-[15px] tracking-tight">Max free listings (per dealer)</div>
        <p className="text-[13px] text-[#52525B] mt-1 max-w-md">Used only when Free Mode is OFF. Lifetime per dealer/builder before plan upgrade required.</p>
        <div className="mt-2 flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={1000}
            value={s.max_free_listings ?? 10}
            onChange={(e) => setS({ ...s, max_free_listings: Number(e.target.value) || 1 })}
            onBlur={() => update({ max_free_listings: Number(s.max_free_listings) || 10 })}
            className="w-24 rounded-sm font-mono-data"
            data-testid="max-free-input"
          />
          <span className="text-[11px] text-[#A1A1AA]">listings</span>
        </div>
      </div>

      {/* Plans visibility */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="setting-plans-enabled">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-heading font-semibold text-[15px] tracking-tight inline-flex items-center gap-2">
              <Tag size={14} className="text-[#0055FF]" /> Subscription Plans
            </div>
            <p className="text-[13px] text-[#52525B] mt-1 max-w-md">
              Master switch. When OFF, /api/plans, /me/plans, /dealer/plans return empty for non-admins. Admin still sees them in /admin?tab=plans.
            </p>
            <div className="mt-2 text-[11px] uppercase tracking-wider font-semibold">
              {s.plans_enabled ? <span className="text-[#15803D]">ON — public</span> : <span className="text-[#92400E]">OFF — hidden</span>}
            </div>
          </div>
          <Switch
            checked={!!s.plans_enabled}
            onCheckedChange={(v) => update({ plans_enabled: v })}
            disabled={busy}
            data-testid="plans-enabled-switch"
          />
        </div>
      </div>

      {/* v2.71.15 — Maintenance: re-resolve coords from google_maps_link */}
      <MapsCoordsBackfillCard />
    </div>
  );
}


function MapsCoordsBackfillCard() {
  const nav = useNavigate();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/properties/bulk-resolve-maps-coords");
      setResult(r.data);
      const { examined, backfilled, manual } = r.data || {};
      if (backfilled > 0) {
        toast.success(`Backfilled coords on ${backfilled} of ${examined} properties`);
      } else if ((manual || 0) === 0 && examined === 0) {
        toast.info("All properties already have map coordinates — nothing to do.");
      } else {
        toast.warning(`Examined ${examined}, none auto-resolvable. ${manual} need a manual location edit — see the list below.`);
      }
    } catch (e) {
      toast.error(apiErrorMsg(e, "Backfill failed"));
    }
    setRunning(false);
  };

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="font-heading font-semibold text-[15px] tracking-tight inline-flex items-center gap-2">
            <MapPin size={14} className="text-[#0055FF]" /> Maps Coordinates Backfill
          </div>
          <p className="text-[13px] text-[#52525B] mt-1 max-w-xl">
            Re-extracts latitude/longitude from each property&rsquo;s Google Maps URL,
            following short links (maps.app.goo.gl) via HTTP redirect. Coordinates are
            only written when confidently resolved — never guessed. Idempotent — safe
            to run anytime.
          </p>
        </div>
        <Button
          onClick={run}
          disabled={running}
          className="rounded-sm bg-[#0055FF] hover:bg-[#003DD9] text-white"
          data-testid="maps-coords-backfill-btn"
        >
          {running ? (
            <>
              <Loader2 size={14} className="mr-1.5 animate-spin" />
              Resolving…
            </>
          ) : (
            <>
              <MapPin size={14} className="mr-1.5" />
              Run Backfill
            </>
          )}
        </Button>
      </div>

      {result && (
        <div className="mt-4 space-y-3" data-testid="backfill-results-panel">
          {/* Final summary */}
          <div className="text-[12px] text-[#52525B] flex gap-3 flex-wrap" data-testid="backfill-summary">
            <span><b className="text-zinc-900 font-mono-data">{result.examined}</b> examined</span>
            <span className="text-emerald-700"><b className="font-mono-data">{result.backfilled}</b> auto-backfilled</span>
            <span className="text-amber-700"><b className="font-mono-data">{result.manual ?? result.unresolvable ?? 0}</b> manual edit</span>
            <span className="text-red-700"><b className="font-mono-data">{result.failed ?? 0}</b> failed</span>
          </div>

          {/* Manual-edit table */}
          {(result.manual_items || []).length > 0 && (
            <div className="border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="backfill-results-table">
              <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[12px] font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle size={12} />
                {result.manual_items.length} listing{result.manual_items.length === 1 ? "" : "s"} need a manual location edit
              </div>
              <div className="max-h-[360px] overflow-y-auto divide-y divide-[#F4F4F5]">
                {result.manual_items.map((m) => (
                  <div key={m.id} className="px-3 py-2.5 flex items-start justify-between gap-3 flex-wrap" data-testid={`backfill-row-${m.id}`}>
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[13px] text-zinc-900">{m.title}</span>
                        <code className="text-[10px] font-mono text-zinc-400" title={m.id}>{(m.id || "").slice(0, 8)}</code>
                      </div>
                      <div className="text-[11px] text-[#52525B] mt-0.5">
                        {m.main_area || "—"}{m.sub_area ? ` · ${m.sub_area}` : ""}
                      </div>
                      {m.google_maps_link ? (
                        <a
                          href={m.google_maps_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] text-[#0055FF] hover:underline break-all block mt-0.5"
                        >
                          {m.google_maps_link.length > 70 ? `${m.google_maps_link.slice(0, 70)}…` : m.google_maps_link}
                        </a>
                      ) : (
                        <span className="text-[11px] text-zinc-400 italic block mt-0.5">No Maps URL</span>
                      )}
                      <div className="text-[11px] text-amber-700 mt-0.5">{m.reason_text}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm shrink-0"
                      onClick={() => nav(m.edit_url || `/admin/listings?edit=${m.id}&step=location`)}
                      data-testid={`backfill-edit-location-${m.id}`}
                    >
                      <MapPin size={12} className="mr-1" /> Edit Location
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
